import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { setPreference, type ThemePreference } from "@/store/theme/themeSlice";

const DARK_SCHEME_QUERY = "(prefers-color-scheme: dark)";

/**
 * The hook feature code reaches for — returns the user's stated preference
 * (`light` | `dark` | `system`, held in Redux; see store/theme/themeSlice.ts),
 * the actually-applied theme with `system` already resolved against the OS
 * setting, and the setter. Keeps every consumer decoupled from the fact
 * that theme happens to live in Redux (and from that slice's own internal
 * field name, `preference` — this hook's public shape,
 * `themePreference`/`setThemePreference`, doesn't change if the slice's
 * shape ever does).
 *
 * Kept in its own file, separate from `ThemeProvider` (which consumes it),
 * so that file only exports a component — mixing a hook export into a
 * component file breaks Vite's fast-refresh boundary assumption.
 */
export function useTheme() {
  const dispatch = useAppDispatch();
  const themePreference = useAppSelector((state) => state.theme.preference);
  const systemPrefersDark = useMediaQuery(DARK_SCHEME_QUERY);

  const resolvedTheme: "light" | "dark" =
    themePreference === "dark" || (themePreference === "system" && systemPrefersDark)
      ? "dark"
      : "light";

  return {
    themePreference,
    resolvedTheme,
    setThemePreference: (preference: ThemePreference) => dispatch(setPreference(preference)),
  };
}
